import {useAutoAnimate} from '@formkit/auto-animate/react';
import Tooltip from 'rc-tooltip';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {
  AddAnother,
  Builder,
} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Builder';
import {Wishlist} from '~/SignUpSheets/Create/routes/Customize/components/Wishlist/Wishlist';
import {Details} from '~/SignUpSheets/components/Details/Details';
import {PageSection, SectionRow} from '~/SignUpSheets/components/PageSection/PageSection';
import {useSheetActions} from '~/SignUpSheets/hooks/useSheetActions';
import {BLUEPRINT_TYPE_ITEMS} from '~/SignUpSheets/reducers/blueprint/constants';
import {selectBlueprintType} from '~/SignUpSheets/reducers/blueprint/selectors';
import {
  DISABLE_GROUPING,
  ENABLE_GROUPING,
  UPDATE_OPEN_GROUPS,
} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectIsInEditMode,
  selectIsMultipleDatesModalOpen,
  selectNumSignupOptions,
  selectNumSignups,
  selectOpenGroups,
  selectSheetStatus,
  selectSignupOptionGroupIds,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {AddMultipleDatesModal} from './components/AddMultipleDatesModal/AddMultipleDatesModal';
import {GroupBuilder} from './components/Builder/GroupBuilder/GroupBuilder';

import './Customize.sass';

let customizeInit = false;
export const CustomizeView = ({trackChangeUpstream}) => {
  const dispatch = useDispatch();
  const [showConfirmDisableGroupingModal, setShowConfirmDisableGroupingModal] = useState(false);
  const {getSavedSheet, autoSave} = useSheetActions();
  const isInEditMode = useSelector(selectIsInEditMode);
  const numSignups = useSelector(selectNumSignups);
  const isDesktop = useIsLargishAndUp();
  const isLoggedIn = useSelector(getLoggedUserId);
  const blueprintType = useSelector(selectBlueprintType);
  const editRouteMatch = useRouteMatch('/:sheetId/edit');
  const numSignUpOptions = useSelector(selectNumSignupOptions);
  const sheetStatus = useSelector(selectSheetStatus);
  const signupOptionGroupIds = useSelector(selectSignupOptionGroupIds(), shallowEqual);
  const isMultipleDatesModalOpen = useSelector(selectIsMultipleDatesModalOpen);
  const openGroups = useSelector(selectOpenGroups);
  const showOrganizerHeader = isInEditMode || sheetStatus === 'draft';

  const [groupsAnimation] = useAutoAnimate({
    duration: 350,
    easing: 'ease-in-out',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!customizeInit && !!editRouteMatch) {
      getSavedSheet(editRouteMatch.params.sheetId);
      customizeInit = true;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handleToggleGrouping = useCallback((e) => {
    if (e.target.checked) {
      dispatch({type: ENABLE_GROUPING, payload: {type: 'time'}});
    } else {
      setShowConfirmDisableGroupingModal(true);
    }
  }, []);

  const handleCloseConfirmDisableGroupingModal = useCallback(() => {
    setShowConfirmDisableGroupingModal(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onConfirmDisableGrouping = useCallback(() => {
    dispatch({type: DISABLE_GROUPING});
    setShowConfirmDisableGroupingModal(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const ItemListPageSectionSubtitle = useMemo(() => {
    if (blueprintType === BLUEPRINT_TYPE_ITEMS) {
      const AddTimeSlotsToggle = (
        <div className="signup-sheet__add-time-slots-switch">
          <Typography variant="paragraph2" color={numSignups > 0 ? 'pebble' : 'asphalt'}>
            Include time windows
          </Typography>
          {/* <TimeWindowTooltip /> */} {/* TODO: This will be restored in a follow-up */}
          <Input
            type="toggle"
            checked={signupOptionGroupIds.length > 0}
            onChange={handleToggleGrouping}
            data-qa-id="sheet-add-time-slots"
            id="sheet-add-time-slots"
            size="small"
            disabled={numSignups > 0}
          />
        </div>
      );
      // NOTE: The copy and handling of this may change in the future as we add
      // support for other kinds of grouping
      return numSignups > 0
        ? <Tooltip
            overlayClassName="slot-toolbar__tooltip"
            placement="top"
            overlay={
              <Typography
                as="span"
                variant="paragraph3"
                data-qa-id="toggle-time-slots-blocked"
                color="charcoal"
              >
                You can&apos;t {signupOptionGroupIds.length > 0 ? 'remove' : 'add'} time slots
                because you already have signups.
              </Typography>
            }
            trigger={['hover']}
          >
            {AddTimeSlotsToggle}
          </Tooltip>
        : AddTimeSlotsToggle;
    }
    return null;
  }, [
    blueprintType,
    signupOptionGroupIds.length,
    handleToggleGrouping,
    numSignups,
    trackChangeUpstream,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onToggleGroup = useCallback(
    (groupId, newStatus) => {
      const currOpenGroups = new Set(openGroups);
      if (newStatus) {
        // If MW, close all other groups and just have the specified one open
        if (!isDesktop) currOpenGroups.clear();
        currOpenGroups.add(groupId);
      } else currOpenGroups.delete(groupId);
      dispatch({
        type: UPDATE_OPEN_GROUPS,
        payload: currOpenGroups,
      });
    },
    [openGroups, isDesktop]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const BuilderSection = useMemo(() => {
    if (signupOptionGroupIds.length === 0) {
      return (
        <SectionRow wide loggedIn={isLoggedIn} showOrganizerHeader>
          <Builder
            trackChangeUpstream={trackChangeUpstream}
            openSignupOptions={openGroups}
            onToggleSignupOption={onToggleGroup}
          />
        </SectionRow>
      );
    }

    return (
      <div ref={groupsAnimation}>
        {signupOptionGroupIds.map((groupId) => (
          <SectionRow
            wide
            loggedIn={isLoggedIn}
            showOrganizerHeader
            key={groupId}
            id={`group-${groupId}`}
            grouped={!!groupId}
          >
            <GroupBuilder
              groupId={groupId}
              isDesktop={isDesktop}
              open={openGroups.has(groupId)}
              onToggle={onToggleGroup}
              trackChangeUpstream={trackChangeUpstream}
            />
          </SectionRow>
        ))}
      </div>
    );
  }, [signupOptionGroupIds, isLoggedIn, isDesktop, openGroups, onToggleGroup]);

  return (
    <>
      {isMultipleDatesModalOpen && (
        <AddMultipleDatesModal trackChangeUpstream={trackChangeUpstream} />
      )}

      <div className="signup-sheet__customize">
        <PageSection
          title={isDesktop ? 'Step 1: Add key details' : null}
          pageSectionClassName="signup-sheet__customize__section signup-sheet__customize__section__details"
          isLoggedIn
          id="details"
          bodyQaId="details"
        >
          <Details />
        </PageSection>

        <PageSection
          title={`${isDesktop ? 'Step 2: ' : ''}Add signup slots`}
          pageSectionClassName="signup-sheet__customize__section"
          isLoggedIn={isLoggedIn}
          loginDataQaId="signup-sheet-builder-login"
          loginCtaText="Log in to create"
          onLogin={() => autoSave()}
          footer={<AddAnother trackChangeUpstream={trackChangeUpstream} />}
          id="builder"
          bodyQaId="signup-options"
          bodyQaValue={numSignUpOptions}
          showOrganizerHeader
          subtitle={ItemListPageSectionSubtitle}
          stackedHeader={blueprintType === BLUEPRINT_TYPE_ITEMS}
          forceShowSubtitle
          responsiveLoginWall
        >
          {BuilderSection}
        </PageSection>
        <PageSection
          title={`${isDesktop ? 'Step 3: ' : ''}Go the extra mile`}
          isLoggedIn={isLoggedIn}
          loginDataQaId="wishlist-login"
          loginCtaText="Log in to edit"
          onLogin={() => autoSave()}
          id="wishlist"
          showOrganizerHeader={showOrganizerHeader}
          pageSectionClassName="signup-sheet__customize__section"
        >
          <SectionRow loggedIn={isLoggedIn} showOrganizerHeader>
            <Wishlist />
          </SectionRow>
        </PageSection>
      </div>
      {showConfirmDisableGroupingModal
        ? <ResponsiveModal
            isOpen
            title="Are you sure?"
            body="Your existing signups will be ungrouped, but no data will be lost."
            // NOTE: The primary text below may be refined later as we add support for different types of grouping
            primaryText="Turn off time slots"
            secondaryText="Nevermind"
            primaryAction={onConfirmDisableGrouping}
            onClose={handleCloseConfirmDisableGroupingModal}
          />
        : null}
    </>
  );
};
